import type { ApiResponse } from '@kangaroo/shared';

import { api } from '../lib/api';

export interface McpAuthorizationRequest {
  requestId: string;
  clientName: string;
  scope: string;
  redirectOrigin: string;
  account: { name: string; email: string };
  teamId: string | null;
  teamName: string | null;
}

export interface McpConnection {
  id: string;
  clientName: string;
  scope: string;
  createdAt: string;
  expiresAt: string;
}

export interface McpConnectionSettings {
  mcpUrl: string;
  selfServiceAvailable: boolean;
  publicPluginUrl: string | null;
}

export async function fetchMcpConnectionSettings() {
  const response = await api.get<ApiResponse<McpConnectionSettings>>('/mcp/connection-settings');
  return response.data.data;
}

export function mcpConnectionAccessLabel(scope: string) {
  const scopes = [...new Set(scope.trim().split(/\s+/).filter(Boolean))];
  if (!scopes.length) return 'Permissions unavailable';
  if (scopes.length === 1 && scopes[0] === 'paddiq:read') return 'Read-only access';
  const labels: Record<string, string> = {
    'paddiq:read': 'Read Paddiq data',
    'paddiq:write': 'Create and change records',
    'paddiq:destructive': 'Delete, release or revoke records',
    'paddiq:operator:read': 'Read platform operator data',
    'paddiq:operator:campaigns': 'Prepare and send operator campaigns',
  };
  return scopes.map((value) => labels[value] ?? `Additional access (${value})`).join('; ');
}

export async function fetchMcpAuthorizationRequest(requestId: string) {
  const response = await api.get<ApiResponse<McpAuthorizationRequest>>(
    `/mcp/authorization-requests/${encodeURIComponent(requestId)}`,
  );
  return response.data.data;
}

export async function decideMcpAuthorizationRequest(requestId: string, approved: boolean) {
  const response = await api.post<ApiResponse<{ redirectUrl: string }>>(
    `/mcp/authorization-requests/${encodeURIComponent(requestId)}/decision`,
    { approved },
  );
  return response.data.data;
}

export async function fetchMcpConnections() {
  const response = await api.get<ApiResponse<McpConnection[]>>('/mcp/connections');
  return response.data.data;
}

export async function disconnectMcpConnection(connectionId: string) {
  await api.delete(`/mcp/connections/${encodeURIComponent(connectionId)}`);
}
